import React, { useMemo, useSyncExternalStore } from 'react';

/**
 * Web math rendering.
 *
 * KaTeX is ~270 KB of source and was the single largest third-party module pinned into
 * the web first-load bundle, even though only a minority of messages contain math. It is
 * now fetched on demand: `import('katex')` puts it in its own async chunk, and the
 * request only starts when a math node actually mounts.
 *
 * This is safe because the no-KaTeX branch is not new — it already existed as the
 * fallback for environments where the `require` failed, and it renders the raw LaTeX as
 * text. Deferring the load reuses that path for the brief window before the chunk lands,
 * so math degrades to readable source instead of disappearing.
 */

let katex = null;
let katexPromise = null;
let katexFailed = false;
const subscribers = new Set();

/**
 * KaTeX's stylesheet used to be injected at module scope, which fired a cross-origin CDN
 * request on every page load whether or not anything rendered math. It is requested here
 * instead — at the *start* of the load, so it downloads in parallel with the JS chunk
 * rather than after it.
 */
function injectKatexStyles() {
  if (typeof document === 'undefined') return;

  if (!document.querySelector('link[href*="katex"]')) {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://cdn.jsdelivr.net/npm/katex@0.16.21/dist/katex.min.css';
    link.crossOrigin = 'anonymous';
    document.head.appendChild(link);
  }

  // KaTeX uses huge SVGs (width: 400em) for fraction lines, sqrt symbols and similar,
  // and relies on an ancestor to clip them. Enforce that at the .katex-html boundary.
  if (!document.querySelector('style[data-katex-fix]')) {
    const style = document.createElement('style');
    style.setAttribute('data-katex-fix', '');
    style.textContent = '.katex-html{overflow:hidden}.katex{max-width:100%}';
    document.head.appendChild(style);
  }
}

function loadKatex() {
  if (katex || katexFailed || katexPromise) return;
  injectKatexStyles();
  katexPromise = import('katex')
    .then((mod) => {
      katex = mod?.default || mod;
      subscribers.forEach((notify) => notify());
    })
    .catch((err) => {
      // Latch the failure. A page can hold dozens of math nodes, and retrying per mount
      // would turn one dead chunk request into a storm.
      katexFailed = true;
      console.warn('KaTeX not available for math rendering', err);
    });
}

const subscribe = (notify) => {
  subscribers.add(notify);
  loadKatex();
  return () => { subscribers.delete(notify); };
};
const getSnapshot = () => katex;
// Server render has no KaTeX and must not start a load; the text fallback is correct there.
const getServerSnapshot = () => null;

/**
 * Returns the KaTeX module once loaded, re-rendering the caller when it arrives.
 * `useSyncExternalStore` is used rather than a `useState`/`useEffect` pair because the
 * module can land between a component's render and its effect, and this handles that
 * ordering by construction.
 */
function useKatex() {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

const isMathML = (input) => /^\s*<math[\s>]/i.test(input);

/** Render LaTeX to HTML, or null if KaTeX is absent or the input does not parse. */
function useRenderedMath(katexModule, latex, displayMode) {
  return useMemo(() => {
    if (!katexModule) return null;
    try {
      return katexModule.renderToString(latex, {
        throwOnError: false,
        displayMode,
        output: 'html',
      });
    } catch {
      return null;
    }
  }, [katexModule, latex, displayMode]);
}

/**
 * Renders inline math using KaTeX on web.
 * MathML is rendered directly (browsers support it natively).
 * Falls back to the raw LaTeX in a serif face until KaTeX is available.
 */
export function InlineMath({ latex, color, fontSize }) {
  // Hooks run before the MathML branch returns. Previously both hooks sat below that
  // early return, so a node whose content changed between MathML and LaTeX changed its
  // hook count between renders — the one input that makes React throw here.
  const katexModule = useKatex();
  const html = useRenderedMath(katexModule, latex, false);

  if (isMathML(latex)) {
    return (
      <span
        dangerouslySetInnerHTML={{ __html: latex }}
        style={{ color, fontSize, overflow: 'hidden', maxWidth: '100%' }}
      />
    );
  }

  if (!html) {
    return (
      <span style={{ fontFamily: 'KaTeX_Main, Times New Roman, serif', color, fontSize }}>
        {latex}
      </span>
    );
  }

  return (
    <span
      dangerouslySetInnerHTML={{ __html: html }}
      style={{ color, overflow: 'hidden', maxWidth: '100%' }}
    />
  );
}

/**
 * Renders display (block) math using KaTeX on web.
 * MathML is rendered directly (browsers support it natively).
 * Falls back to the centered raw LaTeX until KaTeX is available.
 */
export function DisplayMath({ latex, color, fontSize }) {
  const katexModule = useKatex();
  const html = useRenderedMath(katexModule, latex, true);

  if (isMathML(latex)) {
    return (
      <div
        dangerouslySetInnerHTML={{ __html: latex }}
        style={{
          color,
          textAlign: 'center',
          padding: '12px 0',
          overflowX: 'auto',
          maxWidth: '100%',
        }}
      />
    );
  }

  if (!html) {
    return (
      <div style={{
        fontFamily: 'KaTeX_Main, Times New Roman, serif',
        color,
        fontSize,
        textAlign: 'center',
        padding: '12px 0',
        overflowX: 'auto',
      }}>
        {latex}
      </div>
    );
  }

  return (
    <div
      dangerouslySetInnerHTML={{ __html: html }}
      style={{
        color,
        textAlign: 'center',
        padding: '12px 0',
        overflowX: 'auto',
        maxWidth: '100%',
      }}
    />
  );
}
